import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useCurrentUser from 'shared/../hooks/useCurrentUser';
// Helpers
import { differenceBy, isNil } from 'lodash';
// Consts
import navigation from '../navigation';
import tableMaintList from '../TableMaintList/tableMaintList';
// Sections tables maint
import adminTablesMaint from 'src/admin/components/pages/AdminTableMaintsPage/pages';
import safetyTablesMaint from 'src/safety/components/pages/SafetyTableMaintsPage/pages';
import warehouseTablesMaint from 'src/warehouse/components/pages/WarehouseTableMaintsPage/pages';

const useNavigation = () => {
  const { isUserLoading, isFailed, hasAnyAccessTo, userFullName, systemUserName } = useCurrentUser();
  const { pathname } = useLocation();

  const [openNavPath, setOpenNavPath] = useState(null);
  const closeSubNav = () => setOpenNavPath(null);

  const getByAccess = (items) => items.filter(({ userAccess }) => hasAnyAccessTo(userAccess) || isNil(userAccess));

  const buildSubNav = (items = [], parentPath) => getByAccess(items).map((item) => {
    const { path } = item;
    const fullPath = `/${parentPath}/${path}`;
    return {
      ...item,
      id: path,
      path: fullPath,
      isActive: pathname.startsWith(fullPath),
    };
  });

  const buildNav = () => {
    if (isUserLoading) {
      return [];
    }
    return getByAccess(navigation).map((item) => {
      const { path } = item;
      const subNav = buildSubNav(item.subNav, path);
      return {
        ...item,
        id: path,
        path: subNav[0]?.path ?? `/${path}`,
        isActive: !isNil(subNav.find(({ isActive }) => isActive)),
        subNav,
      };
    });
  };

  const navItems = useMemo(buildNav, [isUserLoading, pathname]);

  // Build list of navigations available for search
  const searchList = useMemo(() => {
    if (isUserLoading) {
      return [];
    }
    // Build navs by menu links
    const menuNavs = getByAccess(navigation)
      .map(({ path, subNav }) => {
        if (!subNav) {
          return null;
        }
        const filtered = getByAccess(subNav);
        return filtered.map((nav) => ({ ...nav, section: path, path: `/${path}/${nav.path}` }));
      })
      .filter((d) => !isNil(d))
      .flat();

    // Get access by path
    const getMaintAccess = (maintPath) => {
      const maint = menuNavs.find(({ path }) => path === maintPath);
      if (!maint) {
        return 'none';
      }
      return maint.userAccess;
    };

    // Build nav object by sections tables maint pages
    const adminMaintAccess = getMaintAccess('/admin/table-maints');
    const showAdminMaint = adminMaintAccess !== 'none' && (hasAnyAccessTo(adminMaintAccess) || isNil(adminMaintAccess));
    const adminMaintNavs = showAdminMaint ? adminTablesMaint.map(({ path, text }) => ({
      path, text, section: 'admin', userAccess: adminMaintAccess,
    })) : [];

    const safetyMaintAccess = getMaintAccess('/safety/safety-table-maints');
    const showSafetyMaint = safetyMaintAccess !== 'none'
      && (hasAnyAccessTo(safetyMaintAccess) || isNil(safetyMaintAccess));
    const safetyMaintNavs = showSafetyMaint ? safetyTablesMaint.map(({ path, text }) => ({
      path, text, section: 'safety', userAccess: safetyMaintAccess,
    })) : [];

    const warehouseMaintAccess = getMaintAccess('/warehouse/warehouse-table-maints');
    const showWarehouseMaint = warehouseMaintAccess !== 'none'
      && (hasAnyAccessTo(warehouseMaintAccess) || isNil(warehouseMaintAccess));
    const warehouseMaintNavs = showWarehouseMaint ? warehouseTablesMaint.map(({ path, text }) => ({
      path, text, section: 'warehouse', userAccess: warehouseMaintAccess,
    })) : [];

    const sectionsNavs = [...menuNavs, ...adminMaintNavs, ...safetyMaintNavs, ...warehouseMaintNavs];

    // Build navs from Admin Table Maint search
    const adminSearchNavs = tableMaintList.map((t) => ({ ...t, path: `/${t.section}/${t.path}` }));
    const adminTablesNavs = differenceBy(adminSearchNavs, sectionsNavs, 'path');

    // All navs
    const navs = [...sectionsNavs, ...adminTablesNavs];
    return navs;
  }, [isUserLoading]);

  const getSectionTitle = (sectionPath) => navigation.find(({ path }) => path === sectionPath)?.text;

  return {
    navItems,
    subNavItems: navItems.find(({ path }) => openNavPath === path)?.subNav ?? null,
    openNavPath,
    showSubNav: setOpenNavPath,
    closeSubNav,
    show: !(isUserLoading || isFailed),
    userFullName,
    systemUserName,
    // Search
    searchList,
    getSectionTitle,
  };
};

export default useNavigation;
